/**
 * SectionAiScribe — micro AI-Scribe button for a single PE section.
 * Iter 70.
 *
 * Renders a small mic button next to any textarea. When pressed:
 *   1. Asks for mic permission (browser prompt — only once per session).
 *   2. Records via MediaRecorder.
 *   3. On stop, POSTs to /api/ai/transcribe with `section` hint for Whisper.
 *   4. Receives the raw transcript and calls onTranscript(text) so the parent
 *      can append it to its target field.
 *
 * The full /api/ai/scribe runs Whisper + an LLM structuring pass. This thin
 * version skips the LLM step because the clinician already knows which
 * section the audio belongs to.
 *
 * Props:
 *   patientId    string  (required — for consent + audit)
 *   section      string  ("auscultation" | "rom" | "mmt" | "special_tests" | ...)
 *   onTranscript (text: string) => void
 *   testid       string  (data-testid prefix)
 *   compact      bool    render as a tiny icon-only mic instead of a labeled chip
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Loader2, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

export default function SectionAiScribe({ patientId, section, onTranscript, testid = 'section-scribe', compact = false }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAt = useRef(0);

  useEffect(() => () => { stop(); /* on unmount */ }, []);  // eslint-disable-line

  const start = async () => {
    if (!patientId) {
      toast.error('Pick a patient first.');
      return;
    }
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        if (blob.size < 800) {
          toast.error('Recording too short — try again.');
          setRecording(false);
          return;
        }
        await upload(blob);
      };
      rec.start();
      startedAt.current = Date.now();
      setRecording(true);
    } catch (e) {
      setError(e.message || 'Mic permission denied');
      toast.error('Mic permission denied — check your browser settings.');
    }
  };

  const stop = () => {
    if (recRef.current && recRef.current.state === 'recording') {
      recRef.current.stop();
      setRecording(false);
    }
  };

  const upload = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, `scribe-${section}.webm`);
      fd.append('patient_id', patientId);
      if (section) fd.append('section', section);
      const r = await api.post('/ai/transcribe', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const text = r.data?.transcript || '';
      if (text) {
        onTranscript?.(text);
        toast.success(`Added ${text.length} chars to ${section || 'note'}.`);
      } else {
        toast.error('Empty transcript — try recording closer to the mic.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Transcription failed.';
      // 412 = no consent on file. 403 = feature disabled.
      if (e?.response?.status === 412) {
        toast.error('Patient consent for AI scribe is required first.');
      } else if (e?.response?.status === 403) {
        toast.error('AI scribe is disabled in clinic settings.');
      } else {
        toast.error(msg);
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const toggle = () => recording ? stop() : start();

  if (compact) {
    return (
      <button type="button"
              onClick={toggle}
              disabled={busy}
              data-testid={`${testid}-toggle`}
              className={`relative inline-flex items-center justify-center w-7 h-7 rounded-full transition ${
                recording ? 'bg-[#B83A3A] text-white animate-pulse' :
                busy ? 'bg-fg-muted/20 text-fg-muted' :
                'bg-brand/10 text-brand hover:bg-brand hover:text-white'
              }`}
              title={recording ? 'Stop recording' : busy ? 'Transcribing…' : `AI scribe (${section})`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> :
         recording ? <MicOff className="w-3.5 h-3.5"/> :
         <Mic className="w-3.5 h-3.5"/>}
      </button>
    );
  }

  return (
    <button type="button"
            onClick={toggle}
            disabled={busy}
            data-testid={`${testid}-toggle`}
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium transition ${
              recording ? 'bg-[#B83A3A] text-white animate-pulse' :
              busy ? 'bg-fg-muted/20 text-fg-muted' :
              'bg-brand/10 text-brand hover:bg-brand hover:text-white'
            }`}
            title={error || (recording ? 'Stop recording & transcribe' : `Dictate ${section}`)}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin"/> :
       recording ? <MicOff className="w-3 h-3"/> :
       <Mic className="w-3 h-3"/>}
      {busy ? 'Transcribing…' :
       recording ? 'Stop' :
       'Dictate'}
      {error && !busy && !recording && <AlertCircle className="w-3 h-3 text-[#FFB000]"/>}
    </button>
  );
}
